import {ProactiveScenario} from "./types/ProactiveScenario";
import {PathStep} from "./types/PathStep";
import {ProactiveType} from "./types/ProactiveType";

export class SedestralProactive {
    private static scenarios: ProactiveScenario[] = [];
    private static userHistory: PathStep[] = [];
    private static triggeredScenarios: Set<string> = new Set();
    public static mainTriggerCallback: (id: string) => void = () => {
    };
    private static triggeredScrollDepths: Set<string> = new Set();
    private static exitIntentTriggeredOnPage = false;
    private static triggered = false;

    public static async init(): Promise<void> {
        SedestralProactive.initializeEventListeners();
    }

    public static register(scenario: ProactiveScenario): void {
        if (!SedestralProactive.scenarios.find(s => s.id === scenario.id)) {
            SedestralProactive.scenarios.push(scenario);
        }
    }

    public static destroy(): void {
        document.removeEventListener('scroll', SedestralProactive.handleScroll);
        document.body.removeEventListener('mouseout', SedestralProactive.handleExitIntent);
    }

    public static logPageView(path: string, urlParams?: URLSearchParams): void {
        SedestralProactive.exitIntentTriggeredOnPage = false;
        SedestralProactive.triggeredScrollDepths.clear();

        const step: PathStep = {type: ProactiveType.PAGE_VIEW, data: path};
        if (urlParams) {
            const matchingParam = SedestralProactive.scenarios
                .flatMap(s => s.path)
                .find(p => p.hasParam && urlParams.has(p.hasParam))?.hasParam;
            if (matchingParam) {
                step.hasParam = matchingParam;
            }
        }

        SedestralProactive.addStepToHistory(step);
    }

    public static logCustomEvent(eventName: string): void {
        SedestralProactive.addStepToHistory({type: ProactiveType.CUSTOM_EVENT, data: eventName});
    }

    private static initializeEventListeners(): void {
        document.addEventListener('scroll', SedestralProactive.handleScroll, {passive: true});
        document.body.addEventListener('mouseout', SedestralProactive.handleExitIntent);
    }

    private static addStepToHistory(step: PathStep): void {
        SedestralProactive.userHistory.push(step);
        SedestralProactive.checkScenarios();
    }

    private static handleScroll(): void {
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = document.documentElement.clientHeight;
        if (clientHeight >= scrollHeight) return;

        const scrollY = window.scrollY;
        const currentDepthPercent = Math.round(((scrollY + clientHeight) / scrollHeight) * 100);
        const currentDepthPx = Math.round(scrollY);

        SedestralProactive.scenarios.forEach(scenario => {
            scenario.path.forEach((step, index) => {
                const stepId = `${scenario.id}-${index}`;
                if (step.type === ProactiveType.SCROLL_DEPTH && !SedestralProactive.triggeredScrollDepths.has(stepId)) {
                    const targetDepth = step.data as number;
                    const unit = step.unit || '%';

                    if ((unit === '%' && currentDepthPercent >= targetDepth) || (unit === 'px' && currentDepthPx >= targetDepth)) {
                        SedestralProactive.addStepToHistory({
                            type: ProactiveType.SCROLL_DEPTH,
                            data: targetDepth,
                            unit
                        });
                        SedestralProactive.triggeredScrollDepths.add(stepId);
                    }
                }
            });
        });
    }

    private static handleExitIntent(event: MouseEvent): void {
        if (SedestralProactive.exitIntentTriggeredOnPage) return;
        if (event.clientY <= 0) {
            SedestralProactive.addStepToHistory({type: ProactiveType.EXIT_INTENT, data: 'exit'});
            SedestralProactive.exitIntentTriggeredOnPage = true;
        }
    }

    private static checkScenarios(): void {
        for (const scenario of SedestralProactive.scenarios) {
            if (SedestralProactive.triggeredScenarios.has(scenario.id)) {
                continue;
            }
            SedestralProactive.matchAndTrigger(scenario);
        }
    }

    // =================================================================
    // DEBUT DE LA SECTION MODIFIÉE
    // =================================================================

    private static async matchAndTrigger(scenario: ProactiveScenario) {
        // 1. On sépare l'étape de délai des autres étapes du parcours
        const delayStep = scenario.path.find(p => p.type === ProactiveType.DELAY);
        const pathSteps = scenario.path.filter(p => p.type !== ProactiveType.DELAY);

        const historySlice = SedestralProactive.userHistory;
        let historyIndex = historySlice.length - 1;

        // 2. On vérifie toutes les étapes du parcours (sauf le délai) à l'envers
        for (let i = pathSteps.length - 1; i >= 0; i--) {
            const scenarioStep = pathSteps[i];

            if (scenarioStep.type === ProactiveType.PAGE_VIEW_COUNT) {
                const matchingPages = SedestralProactive.countDistinctPages(scenarioStep.data as string, historySlice);
                if (matchingPages < (scenarioStep.count || 1)) return;
                continue;
            }

            // La vérification du DELAY a été retirée de cette boucle

            if (historyIndex < 0) return;

            if (!SedestralProactive.compareSteps(scenarioStep, historySlice[historyIndex])) {
                return;
            }
            historyIndex--;
        }

        // 3. Si on arrive ici, c'est que toutes les étapes de navigation ont été validées.
        // On "verrouille" le scénario pour éviter qu'il ne soit re-vérifié pendant le délai.
        SedestralProactive.triggeredScenarios.add(scenario.id);

        // 4. On applique le délai s'il existe
        if (delayStep) {
            await new Promise(resolve => setTimeout(resolve, delayStep.data as number));
        }

        if (SedestralProactive.mainTriggerCallback) {
            if (!this.triggered) {
                this.triggered = true;
                SedestralProactive.mainTriggerCallback(scenario.id);
            }
        }
        if (scenario.onTrigger) scenario.onTrigger();
    }

    // =================================================================
    // FIN DE LA SECTION MODIFIÉE
    // =================================================================

    private static countDistinctPages(pattern: string, history: PathStep[]): number {
        const regex = new RegExp(`^${pattern.replace('*', '.*')}$`);
        const distinctPages = new Set<string>();
        history.forEach(step => {
            if (step.type === ProactiveType.PAGE_VIEW && regex.test(step.data as string)) {
                distinctPages.add(step.data as string);
            }
        });
        return distinctPages.size;
    }

    private static compareSteps(scenarioStep: PathStep, historyStep: PathStep): boolean {
        if (scenarioStep.type !== historyStep.type) return false;

        switch (scenarioStep.type) {
            case ProactiveType.PAGE_VIEW: {
                const scenarioPath = scenarioStep.data as string;
                const historyPath = historyStep.data as string;

                const regex = new RegExp(`^${scenarioPath.replace(/\*/g, '.*')}$`);
                if (!regex.test(historyPath)) return false;

                if (scenarioStep.hasParam) {
                    const queryParamMatch = scenarioStep.hasParam === historyStep.hasParam;
                    const pathSegments = historyPath.split('/');
                    const pathSegmentMatch = pathSegments.includes(scenarioStep.hasParam);

                    if (!queryParamMatch && !pathSegmentMatch) {
                        return false;
                    }
                }

                return true;
            }

            case ProactiveType.SCROLL_DEPTH:
                return (scenarioStep.data === historyStep.data && (scenarioStep.unit || '%') === (historyStep.unit || '%'));

            default:
                return scenarioStep.data === historyStep.data;
        }
    }
}